import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { AiOutlineClose, AiOutlineUserAdd, AiOutlineCamera } from 'react-icons/ai';
import SummaryApi from '../common';
import { useTable } from 'react-table';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { getSessionUniversityId } from '../utils/sessionUser';

// ────────────────────────────────────────────────
// THEME — same rules as AllCities.js / InterestedStudents.js
// ────────────────────────────────────────────────
const THEME = {
  primary: 'rgb(23, 44, 87)',        // #172C57
  primaryLight: 'rgba(23, 44, 87, 0.08)',
  actionBlue: '#2563EB',
  actionBlueHover: '#1D4ED8',
  actionGreen: '#10B981',
  actionGreenHover: '#059669',
  actionRed: '#EF4444',
  actionRedHover: '#DC2626',
};

const AllEmployees = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [editPasswordModalIsOpen, setEditPasswordModalIsOpen] = useState(false);
  const [employeesData, setEmployeesData] = useState([]);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [memberType, setMemberType] = useState(null);
  const [availableCount, setAvailableCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    member_type: '',
    full_name: '',
    mobile_number: '',
    password: '',
    employee_image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [universities, setUniversities] = useState([]);
  const [selectedUniversityId, setSelectedUniversityId] = useState(
    getSessionUniversityId() || ''
  );
  const getEmployeeImageUrl = (path) => {
    if (!path) return null;
    const base = String(SummaryApi.employeeImage.url).replace(/\/+$/, "");
    const clean = String(path).replace(/^\/+/, "");
    return `${base}/${clean}`;
  };

  const EmployeeAvatar = ({ name, image, size = 36 }) => {
    const [failed, setFailed] = useState(false);
    const src = getEmployeeImageUrl(image);

    if (!src || failed) {
      return (
        <div
          className="rounded-full flex items-center justify-center font-bold shrink-0"
          style={{
            width: size,
            height: size,
            backgroundColor: THEME.primaryLight,
            color: THEME.primary,
            fontSize: size * 0.4,
          }}
        >
          {(name || "?").charAt(0).toUpperCase()}
        </div>
      );
    }

    return (
      <img
        src={src}
        alt={name}
        onError={() => setFailed(true)}
        className="rounded-full object-cover shrink-0 border border-slate-200"
        style={{ width: size, height: size }}
      />
    );
  };
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isActiveConfirmationModalOpen, setIsActiveConfirmationModalOpen] = useState(false);
  const [isEmployeeActive, setIsEmployeeActive] = useState(false);
  const [selectedCount, setSelectedCount] = useState("");
  const navigate = useNavigate();

  const today = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

  const options = { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' };
  const formatter = new Intl.DateTimeFormat('en-GB', options);
  const todayDate = new Date();
  const minDate = formatter.format(todayDate).split('/').reverse().join('-');

  const [selectedDate, setSelectedDate] = useState('');

  const headers = {
    'Authorization': 'Bearer sandeep_university_916',
    'Content-Type': 'application/json',
  };

  const fetchEmployees = async (universityId = selectedUniversityId) => {
    try {
      const response = universityId
        ? await fetch(SummaryApi.getEmployeeByUniversityId.url, {
          method: SummaryApi.getEmployeeByUniversityId.method,
          headers: headers,
          body: JSON.stringify({ university_id: universityId }),
        })
        : await fetch(SummaryApi.getAllEmployees.url, {
          method: SummaryApi.getAllEmployees.method,
          headers: headers,
        });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();
      if (result.status && Array.isArray(result.data)) {
        setEmployeesData(result.data);
        setAvailableCount(result.total_available_student_count);
      } else {
        setEmployeesData([]);
        setAvailableCount(0);
      }
    } catch (error) {
      setEmployeesData([]);
      toast.error(`Error fetching employees: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchMemberTypeAndEmployees = async () => {
      setLoading(true);
      try {
        const encodedMemberType = sessionStorage.getItem('K3M8X1JQ5VZ4B7PWTL9');
        if (encodedMemberType) {
          const decodedMemberType = atob(encodedMemberType);
          setMemberType(decodedMemberType);
          if (decodedMemberType !== 'Admin' && decodedMemberType !== 'Manager') {
            navigate('/');
          }
        }

        await fetchEmployees();
      } catch (error) {
        toast.error(`Error: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchMemberTypeAndEmployees();
  }, [navigate]);

  useEffect(() => {
    const syncUniversity = () => {
      const id = getSessionUniversityId() || '';
      setSelectedUniversityId(id);
      fetchEmployees(id);
    };

    window.addEventListener('universityChanged', syncUniversity);
    return () => window.removeEventListener('universityChanged', syncUniversity);
  }, []);

  const openModal = () => {
    setFormData({
      member_type: '',
      full_name: '',
      mobile_number: '',
      password: '',
      employee_image: null,
    });
    setImagePreview(null);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setEditPasswordModalIsOpen(false);
    setFormData({
      member_type: '',
      full_name: '',
      mobile_number: '',
      password: '',
      employee_image: null,
    });
    setImagePreview(null);
    setPasswordData({
      newPassword: '',
      confirmPassword: '',
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file.');
        return;
      }
      setFormData((prev) => ({
        ...prev,
        employee_image: file,
      }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  const handleCountChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setSelectedCount(value);
    }
  };

  const validateMobileNumber = (number) => {
    const mobilePattern = /^\d{10}$/;
    return mobilePattern.test(number);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);

    const requiredFields = ['member_type', 'full_name', 'mobile_number', 'password'];

    for (let field of requiredFields) {
      if (!formData[field]) {
        toast.error(`${field.replace('_', ' ')} is required!`);
        setLoadingSubmit(false);
        return;
      }
    }

    if (!validateMobileNumber(formData.mobile_number)) {
      toast.error('Mobile number must be exactly 10 digits!');
      setLoadingSubmit(false);
      return;
    }

    try {
      const payloadFormData = new FormData();
      payloadFormData.append('member_type', formData.member_type);
      payloadFormData.append('full_name', formData.full_name);
      payloadFormData.append('mobile_number', formData.mobile_number);
      payloadFormData.append('password', formData.password);

      if (formData.employee_image) {
        payloadFormData.append('employee_image', formData.employee_image);
      }

      const response = await fetch(SummaryApi.uploadEmployee.url, {
        method: SummaryApi.uploadEmployee.method,
        headers: {
          'Authorization': 'Bearer sandeep_university_916',
        },
        body: payloadFormData,
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const responseData = await response.json();
      if (responseData.status) {
        toast.success('Employee added successfully!');
        closeModal();
        fetchEmployees();
      } else {
        toast.error(responseData.message);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleEditPassword = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);
    const { newPassword, confirmPassword } = passwordData;

    if (!newPassword || !confirmPassword) {
      toast.error('Both password fields are required!');
      setLoadingSubmit(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match!');
      setLoadingSubmit(false);
      return;
    }

    try {
      const response = await fetch(SummaryApi.changeEmployeePassword.url, {
        method: SummaryApi.changeEmployeePassword.method,
        headers: headers,
        body: JSON.stringify({
          employee_id: selectedEmployee.employee_id,
          password: newPassword,
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      toast.success('Password updated successfully!');
      closeModal();
      fetchEmployees();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setLoadingSubmit(false);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleActivateDeactivate = async () => {
    setLoadingSubmit(true);

    const action = isEmployeeActive === "1" ? 'deactivate' : 'activate';
    const status = isEmployeeActive === "1" ? 0 : 1;

    try {
      const response = await fetch(SummaryApi.activateEmployee.url, {
        method: SummaryApi.activateEmployee.method,
        headers: headers,
        body: JSON.stringify({
          employee_id: selectedEmployee.employee_id,
          is_active: status,
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      toast.success(`Employee ${action}d successfully!`);
      setIsActiveConfirmationModalOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleCheckboxChange = (employeeId) => {
    setEmployeesData(prevData =>
      prevData.map(employee => {
        if (employee.employee_id === employeeId) {
          const updatedCheckedStatus = !employee.checked;
          return { ...employee, checked: updatedCheckedStatus };
        }
        return employee;
      })
    );
  };

  const handleSelectAll = () => {
    const allSelected = employeesData.every(employee => employee.checked);

    setEmployeesData(prevData =>
      prevData.map(employee => ({
        ...employee,
        checked: !allSelected,
      }))
    );
  };

  const handleBulkActionSubmit = async () => {
    setLoadingSubmit(true);
    const newlySelectedIds = employeesData
      .filter(employee => employee.checked)
      .map(employee => employee.employee_id);

    if (selectedCount === "" || parseInt(selectedCount) <= 0) {
      toast.error('Please enter a valid count.');
      setLoadingSubmit(false);
      return;
    }

    if (selectedDate === "") {
      toast.error('Please select date.');
      setLoadingSubmit(false);
      return;
    }

    if (newlySelectedIds.length === 0) {
      toast.error('Please select at least one employee.');
      setLoadingSubmit(false);
      return;
    }

    try {
      const response = await fetch(SummaryApi.bulkAction.url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          employee_ids: newlySelectedIds,
          count: parseInt(selectedCount),
          created_date: selectedDate,
          university_id: selectedUniversityId,
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();

      if (result.status) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }

      fetchEmployees();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
    setLoadingSubmit(false);
  };

  const downloadExcelReport = (studentsData, employee_name) => {
    const requiredFields = ['student_name', 'mobile_number'];

    const filteredData = studentsData.map(student => {
      const selectedData = {};
      requiredFields.forEach(field => {
        selectedData[field] = student[field];
      });
      return selectedData;
    });

    const headers = {
      student_name: 'Name',
      mobile_number: 'Phone',
    };

    const formattedData = filteredData.map(student => {
      return Object.fromEntries(
        Object.entries(student).map(([key, value]) => [headers[key], value])
      );
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');

    const fileName = `${employee_name.toLowerCase()}_promotionData.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  const handleDownloadClick = (data) => {
    const students = data?.students;
    const employee_name = data?.full_name;

    downloadExcelReport(students, employee_name);
  };

  const columns = React.useMemo(
    () => [
      ...(memberType === 'Admin' ? [
        {
          Header: 'Select',
          accessor: 'select',
          Cell: ({ row }) => (
            <input
              type="checkbox"
              checked={row.original.checked}
              onChange={() => handleCheckboxChange(row.original.employee_id)}
            />
          ),
        },
      ] : []),
      {
        Header: 'SNO',
        accessor: 'sno',
      },
      {
        Header: 'Employee Name',
        accessor: 'full_name',
        Cell: ({ row }) => {
          const { full_name, member_type, employee_image } = row.original;
          return (
            <div className="flex items-center gap-3">
              <EmployeeAvatar name={full_name} image={employee_image} />
              <span>
                {full_name}
                {member_type === "Digital Leads" && (
                  <span className="text-gray-500 ml-1">({member_type})</span>
                )}
              </span>
            </div>
          );
        }
      },
      {
        Header: 'Mobile Number',
        accessor: 'mobile_number',
      },
      {
        Header: 'Pending Follow-Ups',
        accessor: 'not_follow_up_count',
        Cell: ({ value }) => (
          <span className="text-sm font-semibold text-red-500">{value}</span>
        ),
      },
      {
        Header: 'Student Enrollment Count',
        accessor: 'student_count',
        Cell: ({ row }) => (
          row.original.student_count === "0" ? (
            <span className="text-gray-500">No Student Enrollments</span>
          ) : (
            <button
              style={{ color: THEME.actionBlue }}
              onMouseEnter={(e) => (e.currentTarget.style.color = THEME.actionBlueHover)}
              onMouseLeave={(e) => (e.currentTarget.style.color = THEME.actionBlue)}
              className="font-semibold transition-colors"
              onClick={() => navigate(`/employee/${btoa(row.original.employee_id)}`)}
            >
              {row.original.student_count}
            </button>
          )
        ),
      },
      {
        Header: 'Today Tasks Count',
        Cell: ({ row }) => (
          <span className="text-sm font-semibold text-yellow-600">
            {row.original.today_student_assigned_count}
          </span>
        ),
      },
      {
        Header: 'Tomorrow Tasks Count',
        Cell: ({ row }) => (
          <span className="text-sm font-semibold text-green-600">
            {row.original.students.length}
          </span>
        ),
      },
      {
        Header: 'Actions',
        Cell: ({ row }) => (
          <div>
            <button
              style={
                row.original.is_active === "0"
                  ? { color: '#93C5FD', cursor: 'not-allowed' }
                  : { color: THEME.actionBlue }
              }
              className="mr-2 px-2 py-1 rounded-lg font-semibold whitespace-nowrap transition-colors"
              onClick={() => {
                setSelectedEmployee(row.original);
                setEditPasswordModalIsOpen(true);
              }}
              disabled={row.original.is_active === "0"}
            >
              Edit Password
            </button>
          </div>
        ),
      },
      {
        Header: 'Status',
        accessor: 'is_active',
        Cell: ({ row }) => (
          row.original.is_active === "0" ? (
            <span className="font-semibold text-red-500">InActive</span>
          ) : (
            <span className="font-semibold text-green-500">Active</span>
          )
        ),
      },
      ...(memberType === 'Admin' ? [
        {
          Header: 'Edit Status',
          Cell: ({ row }) => {
            const isActive = row.original.is_active === "1";
            return (
              <button
                style={{ backgroundColor: isActive ? THEME.actionRed : THEME.actionGreen }}
                onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = isActive
                  ? THEME.actionRedHover
                  : THEME.actionGreenHover)
                }
                onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = isActive
                  ? THEME.actionRed
                  : THEME.actionGreen)
                }
                className="text-white px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors shadow-sm"
                onClick={() => {
                  setSelectedEmployee(row.original);
                  setIsEmployeeActive(row.original.is_active);
                  setIsActiveConfirmationModalOpen(true);
                }}
              >
                {isActive ? 'Deactivate' : 'Activate'}
              </button>
            );
          },
        },
        {
          Header: 'Download Excel',
          Cell: ({ row }) => (
            <button
              style={{
                backgroundColor:
                  row.original.students.length === 0 ? '#D1D5DB' : THEME.actionGreen,
                cursor: row.original.students.length === 0 ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={(e) => {
                if (row.original.students.length > 0)
                  e.currentTarget.style.backgroundColor = THEME.actionGreenHover;
              }}
              onMouseLeave={(e) => {
                if (row.original.students.length > 0)
                  e.currentTarget.style.backgroundColor = THEME.actionGreen;
              }}
              className="text-white px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors shadow-sm"
              onClick={() => {
                if (row.original.students.length > 0) {
                  handleDownloadClick(row.original);
                }
              }}
              disabled={row.original.students.length === 0}
            >
              Download
            </button>
          ),
        },
      ] : []),
    ],
    [memberType]
  );

  const filteredEmployees = employeesData.filter((employee) =>
    employee.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const updatedEmployeesData = filteredEmployees.map((employee, index) => ({
    ...employee,
    sno: index + 1,
  }));

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data: updatedEmployeesData,
  });

  return (
    <div className="container">
      {loading ? (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-20 flex justify-center items-center">
          <span className="text-white text-lg">Fetching Data...</span>
        </div>
      ) : (
        <>
          <div className="container ">
            {loadingSubmit && (
              <div className="fixed inset-0 bg-black bg-opacity-70 z-20 flex justify-center items-center">
                <span className="text-white text-lg">Submitting...</span>
              </div>
            )}
            <div className='flex justify-between items-center mb-6'>
              <h1
                className="text-3xl font-bold"
                style={{ color: THEME.primary }}
              >
                All Employees
              </h1>
              {memberType === 'Admin' && (
                <button
                  style={{ backgroundColor: THEME.primary }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                  className={`text-white text-lg px-4 py-2 rounded-lg flex items-center transition-opacity ${loading ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                  onClick={openModal}
                  disabled={loading}
                >
                  <AiOutlineUserAdd className="inline-block mr-2" />
                  Add Personnel
                </button>
              )}
            </div>

            {memberType === 'Admin' && (
              <div>
                <div className='flex justify-between items-center'>
                  <div>
                    <label
                      htmlFor="assignCountInput"
                      className="mr-2 font-semibold"
                      style={{ color: THEME.primary }}
                    >
                      Assign Count:
                    </label>
                    <input
                      id="assignCountInput"
                      type="number"
                      value={selectedCount}
                      onChange={handleCountChange}
                      className='border border-gray-300 rounded-lg py-2 px-4'
                      placeholder="Enter count"
                      min="1"
                    />
                  </div>
                  <div className='text-lg font-bold bg-slate-100 p-2 rounded-lg'>
                    Available Student Count:{' '}
                    <span className='text-red-600'>{availableCount}</span>
                  </div>
                </div>

                <div className='my-4'>
                  <label
                    htmlFor="date"
                    className="font-semibold"
                    style={{ color: THEME.primary }}
                  >
                    Task Assign Date:
                  </label>
                  <input
                    type="date"
                    id="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    min={minDate}
                    className='ml-4 border p-2 rounded-lg'
                    required
                  />
                </div>

                <div className='flex justify-between items-center'>
                  <div className="my-4">
                    <label
                      className="font-semibold"
                      style={{ color: THEME.primary }}
                    >
                      Select All:{' '}
                    </label>
                    <input
                      type="checkbox"
                      checked={employeesData.every(employee => employee.checked)}
                      onChange={handleSelectAll}
                    />
                  </div>

                  <div>
                    <button
                      onClick={handleBulkActionSubmit}
                      style={{ backgroundColor: THEME.primary }}
                      onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                      onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                      className='text-white py-2 px-4 rounded-lg transition-opacity'
                    >
                      Assign Task
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-between items-center mb-4">
              <input
                type="text"
                placeholder="Search by employee name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2 w-full max-w-md"
              />

              <div
                className="ml-4 text-sm font-semibold"
                style={{ color: THEME.primary }}
              >
                Total Employees: {updatedEmployeesData.length}
              </div>
            </div>
            <div className="overflow-x-auto ">
              <table {...getTableProps()} className="min-w-full mb-4 rounded-lg shadow-md overflow-hidden">
                <thead className="bg-gray-200">
                  {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map(column => (
                        <th
                          {...column.getHeaderProps()}
                          className="px-4 py-2 text-left font-semibold whitespace-nowrap"
                          style={{ color: THEME.primary }}
                        >
                          {column.render('Header')}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map(row => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} className="hover:bg-gray-100 transition">
                        {row.cells.map(cell => (
                          <td {...cell.getCellProps()} className="border px-4 py-2">
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Activate/Deactivate Confirmation Modal */}
            <Modal
              isOpen={isActiveConfirmationModalOpen}
              onRequestClose={() => setIsActiveConfirmationModalOpen(false)}
              contentLabel="Activate/Deactivate Employee"
              className="mx-auto my-10 mt-[100px] max-w-xl rounded-lg bg-white shadow-lg overflow-hidden outline-none"
              overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
            >
              <div className="px-6 py-4 bg-white border-b border-slate-100 flex justify-between items-center">
                <h2
                  className="text-lg font-bold"
                  style={{ color: THEME.primary }}
                >
                  {isEmployeeActive === "1" ? 'Deactivate' : 'Activate'} Employee
                </h2>
                <button onClick={() => setIsActiveConfirmationModalOpen(false)}>
                  <AiOutlineClose className="text-gray-600 hover:text-gray-800" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-gray-700">
                  Are you sure you want to {isEmployeeActive === "1" ? 'deactivate' : 'activate'} this employee?
                </p>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setIsActiveConfirmationModalOpen(false)}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleActivateDeactivate}
                    style={{
                      backgroundColor:
                        isEmployeeActive === "1" ? THEME.actionRed : THEME.actionGreen,
                    }}
                    onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      isEmployeeActive === "1"
                        ? THEME.actionRedHover
                        : THEME.actionGreenHover)
                    }
                    onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      isEmployeeActive === "1"
                        ? THEME.actionRed
                        : THEME.actionGreen)
                    }
                    className="text-white px-4 py-2 rounded-lg font-semibold transition-colors shadow-sm"
                  >
                    {isEmployeeActive === "1" ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            </Modal>

            {/* Add Employee Modal */}
            <Modal
              isOpen={modalIsOpen}
              onRequestClose={closeModal}
              contentLabel="Add Personnel"
              className="w-full max-w-lg md:max-w-xl mx-auto my-10 mt-[100px] bg-white rounded-lg shadow-lg overflow-hidden outline-none"
              overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
            >
              <div className="px-6 py-4 bg-white border-b border-slate-100 flex items-center justify-between">
                <h2
                  className="text-lg font-bold"
                  style={{ color: THEME.primary }}
                >
                  Add Personnel
                </h2>
                <button
                  type="button"
                  onClick={closeModal}
                  className="p-1 rounded-full hover:bg-gray-100 transition"
                >
                  <AiOutlineClose className="text-gray-600 text-xl hover:text-red-500" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-6">
                {/* Profile Pic Upload Section */}
                <div className="mb-5 flex flex-col items-center">
                  <div className="relative group">
                    <div className="w-24 h-24 rounded-full border-2 border-dashed border-gray-300 overflow-hidden flex items-center justify-center bg-gray-50 shadow-inner">
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="Profile Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex flex-col items-center text-gray-400">
                          <AiOutlineCamera size={30} />
                          <span className="text-[10px] mt-1 font-medium">Upload Photo</span>
                        </div>
                      )}
                    </div>
                    <label
                      htmlFor="employee_image"
                      className="absolute inset-0 w-full h-full rounded-full cursor-pointer flex items-center justify-center bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <AiOutlineCamera size={24} />
                    </label>
                  </div>
                  <input
                    type="file"
                    id="employee_image"
                    name="employee_image"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="employee_image"
                    className="mt-2 text-xs font-semibold cursor-pointer transition-colors"
                    style={{ color: THEME.actionBlue }}
                  >
                    {imagePreview ? 'Change Profile Picture' : 'Add Profile Picture (Optional)'}
                  </label>
                </div>

                <div className="mb-4">
                  <label
                    className="block text-sm font-semibold mb-1"
                    style={{ color: THEME.primary }}
                  >
                    Member Type
                  </label>
                  <select
                    name="member_type"
                    value={formData.member_type}
                    onChange={handleChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                    required
                  >
                    <option value="" disabled>Select member type</option>
                    <option value="Admin">Admin</option>
                    <option value="Manager">Manager</option>
                    <option value="Employee">Employee</option>
                    <option value="Team Lead">Team Lead</option>
                    <option value="Digital Leads">Digital Leads</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label
                    className="block text-sm font-semibold mb-1"
                    style={{ color: THEME.primary }}
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label
                    className="block text-sm font-semibold mb-1"
                    style={{ color: THEME.primary }}
                  >
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    name="mobile_number"
                    value={formData.mobile_number}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value) && value.length <= 10) {
                        setFormData((prev) => ({
                          ...prev,
                          mobile_number: value,
                        }));
                      }
                    }}
                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                    required
                    pattern="\d{10}"
                    title="Mobile number must be exactly 10 digits."
                  />
                </div>

                <div className="mb-4">
                  <label
                    className="block text-sm font-semibold mb-1"
                    style={{ color: THEME.primary }}
                  >
                    Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                    required
                  />
                </div>

                <button
                  type="submit"
                  style={{ backgroundColor: THEME.primary }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                  className="mt-4 w-full text-white px-4 py-2 rounded-lg font-semibold transition-opacity shadow-sm"
                >
                  Add Employee
                </button>
              </form>
            </Modal>

            {/* Edit Password Modal */}
            <Modal
              isOpen={editPasswordModalIsOpen}
              onRequestClose={closeModal}
              contentLabel="Edit Password"
              className="w-full max-w-lg md:max-w-xl mx-auto my-10 mt-[100px] bg-white rounded-lg shadow-lg overflow-hidden outline-none"
              overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
            >
              <div className="px-6 py-4 bg-white border-b border-slate-100 flex items-center justify-between">
                <h2
                  className="text-lg font-bold"
                  style={{ color: THEME.primary }}
                >
                  Edit Password for {selectedEmployee ? selectedEmployee.full_name : ''}
                </h2>
                <button onClick={closeModal}>
                  <AiOutlineClose className="text-gray-600 hover:text-gray-800" />
                </button>
              </div>
              <form onSubmit={handleEditPassword} className="p-6">
                <div className="mb-4">
                  <label
                    className="block text-sm font-semibold mb-1"
                    style={{ color: THEME.primary }}
                  >
                    New Password
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label
                    className="block text-sm font-semibold mb-1"
                    style={{ color: THEME.primary }}
                  >
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2 mt-1 focus:outline-none focus:ring-2 focus:ring-[#172C57]"
                    required
                  />
                </div>

                <button
                  type="submit"
                  style={{ backgroundColor: THEME.primary }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                  className={`mt-4 w-full text-white px-4 py-2 rounded-lg font-semibold transition-opacity shadow-sm ${loadingSubmit && 'cursor-not-allowed opacity-60'
                    }`}
                  disabled={loadingSubmit}
                >
                  {loadingSubmit ? 'Updating Password...' : 'Update Password'}
                </button>
              </form>
            </Modal>
          </div>
        </>
      )}
    </div>
  );
};

export default AllEmployees;